// Referee x Club — types + shaping (Phase 3, half-donut + stats).
//
// For one referee + one team + a season window: a W/D/L half-donut of the team's
// record under that ref, plus For/Vs cards & pens. Pure + framework-free; the
// donut arc geometry + D3 animation live in the component.

/** One referee×team record for a season window. */
export interface RefWithTeamsEntry {
  games: number;
  w: number;
  d: number;
  l: number;
  ycF: number;
  ycA: number;
  rcF: number;
  rcA: number;
  pF: number;
  pA: number;
}

/** Keyed referee → team → season window ("s" this / "l" last / "a" all-time). */
export type RefWithTeamsData = Record<
  string,
  Record<string, { s: RefWithTeamsEntry; l: RefWithTeamsEntry; a: RefWithTeamsEntry }>
>;

/** Shaped view: the record percentages + the raw For/Vs figures. */
export interface RefWithTeamsView {
  ref: string;
  games: number;
  wPct: number;
  dPct: number;
  lPct: number;
  ycF: number;
  ycA: number;
  rcF: number;
  rcA: number;
  pF: number;
  pA: number;
}

/** Map the season control to a data key. */
function seasonKey(season: string | null | undefined): "s" | "l" | "a" {
  return season === "Last Season" ? "l" : season === "All Time" ? "a" : "s";
}

/**
 * Shape the referee×team record for the season. Returns null when the ref/team
 * pair is missing (empty state). W/D/L are rounded percentages of games.
 */
export function shapeRefWithTeams(
  data: RefWithTeamsData,
  ref: string,
  team: string,
  season: string | null | undefined
): RefWithTeamsView | null {
  const dd = data[ref]?.[team]?.[seasonKey(season)];
  if (!dd) return null;
  const g = dd.games;
  return {
    ref,
    games: g,
    wPct: g ? Math.round((dd.w / g) * 100) : 0,
    dPct: g ? Math.round((dd.d / g) * 100) : 0,
    lPct: g ? Math.round((dd.l / g) * 100) : 0,
    ycF: dd.ycF,
    ycA: dd.ycA,
    rcF: dd.rcF,
    rcA: dd.rcA,
    pF: dd.pF,
    pA: dd.pA,
  };
}
